import { toastState } from "@/atoms/system/toast-state";
import { PaginationSize } from "@/common/enum/pagination-size";
import { ItemDiscountsBar } from "@/components/bar/ItemDiscountsBar";
import { Divider } from "@/components/devider/Devider";
import { ItemDiscountsHeader } from "@/components/header/ItemDiscountsHeader";
import { ItemAllDiscountsTable } from "@/components/table/item-all-discounts/ItemAllDiscountsTable";
import { ItemSelectedDiscountsTable } from "@/components/table/item-selected-discounts/ItemSelectedDiscountsTable";
import { Body16Regular } from "@/components/typography/Body16Regular";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { itemService } from "@/services/item-service";
import { BasePageDto } from "@/types/dto/base-page-dto";
import { ItemDetailDto } from "@/types/dto/item-detail-dto";
import { UpdateItemDiscountsPayload } from "@/types/payload/update-item-discounts-payload";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { ItemAdminOrderType } from "../../../common/enum/item-admin-order-type";
import { ItemType } from "../../../common/enum/item-type";
import { selectedItemsState } from "@/atoms/item/selected-items-state";

const initItemPage: BasePageDto<ItemDetailDto> = {
  contents: [],
  currentPage: 1,
  totalElements: 0,
  totalPage: 1,
  isFirst: true,
  isLast: true,
};

export default function SingleProductDiscountsPage() {
  const [itemPage, setItemPage] =
    useState<BasePageDto<ItemDetailDto>>(initItemPage);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [selectedItems, setSelectedItems] = useState<ItemDetailDto[]>([]);
  const [newDiscountRate, setNewDiscountRate] = useState<number>(0);
  const setToast = useSetRecoilState(toastState);
  const [globalSelectedItems, setGlobalSelectedItems] =
    useRecoilState(selectedItemsState);
  const isActive = selectedItems.length > 0;
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialItemsFetched, setInitialItemsFetched] = useState(false);

  const getTotalElements = (): number => {
    if (categoryIds.length > 0) {
      const filteredItemsCount = itemPage.contents.filter((item) =>
        selectedItems.some((selectedItem) => selectedItem.id === item.id)
      ).length;
      return itemPage.contents.length - filteredItemsCount;
    } else {
      return itemPage.totalElements - selectedItems.length;
    }
  };

  const updateNewDiscountRate = (newDiscountRate: number) => {
    setNewDiscountRate(newDiscountRate);
  };

  const handleDiscountsButtonClick = async () => {
    flushSync(() => {
      setIsLoading(true);
    });
    try {
      const updateItemDiscountsPayload: UpdateItemDiscountsPayload = {
        ids: selectedItems.map((item) => item.id),
        discountRate: newDiscountRate,
      };
      const updateDiscountsResponse = await itemService.updateDiscounts(
        updateItemDiscountsPayload
      );
      if (!updateDiscountsResponse) return;
      await getItemPageByCategory();
      setToast({
        show: true,
        messages: [`변경된 할인율이 반영되었습니다.`],
      });
      navigate("/product/single-product");
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (categoryIds: number[]) => {
    setCategoryIds(categoryIds);
  };

  const handlePageChange = (currentPage: number) => {
    setItemPage({ ...itemPage, currentPage });
  };

  const handleAddToSelectedItems = (selectedItemIds: number[]) => {
    const newSelectedItems = itemPage.contents.filter((item) =>
      selectedItemIds.includes(item.id)
    );

    setSelectedItems([...selectedItems, ...newSelectedItems]);
  };

  const handleDeleteFromSelectedItems = (itemId: number) => {
    const itemToDelete = selectedItems.find((item) => item.id === itemId);

    if (!itemToDelete) return;

    const updatedSelectedItems = selectedItems.filter(
      (item) => item.id !== itemId
    );

    setSelectedItems(updatedSelectedItems);
  };

  const handleDeleteAllFromSelectedItems = () => {
    setSelectedItems([]);
  };

  const getItemPageByCategory = async () => {
    try {
      const response = await itemService.getPageAdmin({
        page: itemPage.currentPage,
        size: PaginationSize.SIZE_1000,
        categoryIds,
        itemType: ItemType.ITEM,
        orderType: ItemAdminOrderType.ALL,
      });

      if (response.ok) {
        if (globalSelectedItems.length > 0 && !initialItemsFetched) {
          const selectedItemsData = response.data.contents.filter(
            (item) => globalSelectedItems.includes(item.id) && item.price > 0
          );

          if (selectedItemsData.length > 0) {
            setSelectedItems(selectedItemsData);

            const filteredContents = response.data.contents.filter(
              (item) => !globalSelectedItems.includes(item.id)
            );

            setItemPage({
              ...response.data,
              contents: filteredContents,
            });

            setInitialItemsFetched(true);

            setGlobalSelectedItems([]);
            return;
          }
        }

        const filteredContents = response.data.contents.filter(
          (item) =>
            !selectedItems
              .map((selectedItem) => selectedItem.id)
              .includes(item.id)
        );

        setItemPage({
          ...response.data,
          contents: filteredContents,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getItemPageByCategory();
  }, [categoryIds, selectedItems]);

  const totalElements = getTotalElements();

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px]">
        <ItemDiscountsHeader
          isActive={isActive && !isLoading}
          handleDiscountsButtonClick={handleDiscountsButtonClick}
        />
        <ItemDiscountsBar
          totalElements={totalElements}
          handleCategoryChange={handleCategoryChange}
        />
        <ItemAllDiscountsTable
          itemPage={itemPage}
          handlePageChange={handlePageChange}
          handleAddToSelectedItems={handleAddToSelectedItems}
        />
        <div className="flex items-center gap-[16px] my-[21px]">
          <Divider colorClass="bg-semantic-divider" />
          <Body16Regular className="text-semantic-text-body">
            할인 변경 상품
          </Body16Regular>
          <Divider colorClass="bg-semantic-divider" />
        </div>
        <ItemSelectedDiscountsTable
          selectedItems={selectedItems}
          newDiscountRate={newDiscountRate}
          updateNewDiscountRate={updateNewDiscountRate}
          handleDeleteFromSelectedItems={handleDeleteFromSelectedItems}
          handleDeleteAllFromSelectedItems={handleDeleteAllFromSelectedItems}
        />
      </div>
    </PageWrapper>
  );
}
